/** Mirrors `PRODUCT_CONTRACT_VERSION` in api/yeshuman/contract.py. */
export const PRODUCT_CONTRACT_VERSION = '2.13.0'

type Version = [number, number, number]

const CLAUSE = /^\s*(>=|<=|==|!=|>|<)?\s*(\d+(?:\.\d+){0,2})\s*$/

function parse(version: string): Version {
  const parts = version.split('.').map((p) => Number.parseInt(p, 10))
  while (parts.length < 3) parts.push(0)
  return [parts[0], parts[1], parts[2]]
}

function compare(a: Version, b: Version): number {
  for (let i = 0; i < 3; i += 1) {
    if (a[i] !== b[i]) return a[i] - b[i]
  }
  return 0
}

const OPS: Record<string, (c: number) => boolean> = {
  '>=': (c) => c >= 0,
  '<=': (c) => c <= 0,
  '>': (c) => c > 0,
  '<': (c) => c < 0,
  '==': (c) => c === 0,
  '!=': (c) => c !== 0,
}

/**
 * Same rules as the API: a bare version means `>=v,<nextMajor`; otherwise
 * comma-separated comparisons. Returns a problem string, or null when satisfied.
 */
export function requiresProductProblem(
  spec: string | undefined,
  version: string = PRODUCT_CONTRACT_VERSION,
): string | null {
  const trimmed = (spec ?? '').trim()
  if (!trimmed) return 'pack does not declare requiresProduct'
  const current = parse(version)
  const clauses = trimmed.split(',').filter((c) => c.trim())
  for (const clause of clauses) {
    const match = CLAUSE.exec(clause)
    if (!match) return `requiresProduct '${trimmed}' is not a version or comparison list`
    const [, op, targetRaw] = match
    const target = parse(targetRaw)
    const cmp = compare(current, target)
    if (!op) {
      if (clauses.length > 1) return `requiresProduct '${trimmed}' mixes a bare version with comparisons`
      if (!(cmp >= 0 && current[0] === target[0])) {
        return `platform contract ${version} does not satisfy requiresProduct '${trimmed}'`
      }
      continue
    }
    if (!OPS[op](cmp)) {
      return `platform contract ${version} does not satisfy requiresProduct '${trimmed}'`
    }
  }
  return null
}
